<template>
  <VcWidget id="order-summary" class="rounded-lg print:break-inside-avoid">
    <slot name="footer" />
    <slot name="header" />

    <!-- Subtotal block -->
    <div class="order-summary__body mb-4 flex flex-col gap-4 border-b pb-4">
      <VcLoaderOverlay :visible="changing || externalLoadingEvent" />

      <div class="flex justify-between">
        <span class="font-base flex flex-wrap gap-0.5 font-medium"
          >{{ $t("common.labels.subtotal") }}<span>{{ totalItems }} </span>
        </span>
        <span class="font-base font-medium">
          <VcPriceDisplay
            :formatted-value="formatPrice(useCnyExtraTaxPrice ? cartSubtotalWithTax : subTotal, currentCurrency)"
          />
        </span>
      </div>

      <!-- Discount -->
      <div v-if="hasDiscounts" class="flex justify-between">
        <div class="font-base flex appearance-none items-center font-medium">
          {{ hasOnlyNewLabDiscount ? $t("common.labels.new_lab_discount") : $t("common.labels.discount") }}
          <VcIcon name="tag" class="ml-1 size-4"></VcIcon>
        </div>

        <span class="font-base font-medium"
          >{{ (discountDisplay?.amount ?? 0) > 0 ? "-" : "" }}
          <VcPriceDisplay :formatted-value="formatPrice(discountDisplay, currentCurrency)" />
        </span>
      </div>

      <!-- Handling -->
      <div v-if="showTaxAndShipping && handlingTotal && handlingTotal.amount > 0" class="flex justify-between">
        <div class="font-base flex appearance-none items-center font-medium">
          {{ $t("common.labels.handling") }}
        </div>

        <span class="font-base font-medium">
          <VcPriceDisplay :formatted-value="formatPrice(handlingTotal, currentCurrency)" />
        </span>
      </div>

      <!-- Shipping Cost -->
      <div v-if="showTaxAndShipping" class="shipping-cost flex justify-between">
        <span class="font-base flex appearance-none flex-wrap items-center gap-0.5 font-medium"
          >{{ $t("common.labels.shipping_cost") }}
          <span v-if="checkoutShipment?.shipmentMethodOption && checkoutShipment?.shipmentMethodOption !== 'Special'"
            >({{ getShippingOptionFriendlyName(checkoutShipment!.shipmentMethodOption, true) }})</span
          ></span
        >
        <span v-if="!shouldDisplayShippingAccount" class="font-base font-medium">
          <VcPriceDisplay v-if="shippingPrice > 0" :formatted-value="formatPrice(shippingPrice, currentCurrency)" />
          <span v-else>
            <VcIcon name="minus-alt" class="ml-1 w-3"></VcIcon>
          </span>
        </span>
        <span v-else class="font-medium"
          >{{ $t("common.labels.tbd") }}<span v-html-safe="shippingAccountNumberSymbol"></span
        ></span>
      </div>

      <!-- Tax -->
      <template v-if="showTaxAndShipping">
        <!-- India GST split by rate, incl. shipping and handling fee GST -->
        <template v-if="gstBreakdown.length > 0">
          <div v-for="gst in gstBreakdown" :key="gst.rate" class="tax flex justify-between">
            <span class="font-base flex appearance-none items-center font-medium">
              {{ $t("common.labels.gst_breakdown", { rate: gst.rate }) }}
            </span>
            <span class="font-base font-medium">
              <VcPriceDisplay :formatted-value="formatPrice(gst.amount, currentCurrency)" />
            </span>
          </div>
        </template>
        <div v-else class="tax flex justify-between">
          <span class="font-base flex appearance-none items-center font-medium">{{ taxLineText }}</span>
          <span v-if="!shouldDisplayTaxExempt" class="font-base font-medium">
            <VcPriceDisplay
              v-if="checkoutShipment?.hasDeliveryAddress"
              :formatted-value="formatPrice(taxTotal, currentCurrency)"
            />
            <span v-else>-</span>
          </span>
          <span v-else class="font-medium"
            >{{ $t("common.labels.exempt") }}<span v-html-safe="taxExemptSymbol"></span
          ></span>
        </div>
      </template>
    </div>

    <div v-if="totalDisplay" class="total font-base mb-4 flex justify-between font-bold">
      <span>{{ $t("pages.checkout.place_order_total") }}</span>
      <span>
        <VcPriceDisplay :formatted-value="formatPrice(totalDisplay, currentCurrency)" />
      </span>
    </div>

    <div v-if="hasDiscounts && totalDisplay" class="font-base after-discount mb-4 flex justify-between font-bold">
      <span>{{ $t("pages.cart.after_discount") }}</span>
      <VcPriceDisplay :formatted-value="formatPrice(totalDisplay, currentCurrency)" />
    </div>

    <!-- Add display case for punchout JPY handling fee message -->
    <div
      v-if="punchoutCookies && currentCurrency.code === 'JPY'"
      class="policy-message block text-xs font-normal text-gray-400 md:mt-4"
    >
      <span
        v-html-safe="$t('pages.cart.order_summary_message_punchout_jpy')"
        class="policy-link text-xs leading-5 text-[--color-neutral-a2]"
      >
      </span>
    </div>
    <!-- If not in punchout JPY handeling fee case, display the normal flow. Continue to check the condition of shouldShowFootnote and display html content accordingly -->
    <div
      v-if="!(punchoutCookies && currentCurrency.code === 'JPY') && shouldShowFootnote"
      class="policy-message block text-xs font-normal text-gray-400 md:mt-4"
    >
      <span
        v-if="showEstimatedWeight && formattedCartWeight"
        class="policy-link mb-3 block text-sm leading-5 text-[--color-neutral-a2]"
      >
        {{ $t("shared.cart.estimated_shipping_weight") }}: {{ formattedCartWeight }}
      </span>
      <span
        v-if="!showTaxAndShipping"
        v-html-safe="orderSummaryMessage()"
        class="policy-link text-xs leading-5 text-[--color-neutral-a2]"
      >
        <span
          v-if="hasDiscounts"
          v-html-safe="getDiscountPolicyText()"
          class="policy-link text-xs leading-5 text-[--color-neutral-a2]"
        ></span>
      </span>
      <span
        v-else
        v-html-safe="checkoutOrderSummaryMessage"
        class="policy-link test text-xs leading-5 text-[--color-neutral-a2]"
      ></span>
    </div>
  </VcWidget>
</template>

<script setup lang="ts">
import { sumBy } from "lodash";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency } from "@/core/composables";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { getShippingOptionFriendlyName } from "@/core/utilities";
import FeatureFlags from "@/core/utilities/featureFlags";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { formatPrice } from "@/shared/cart/utils/formatPrice";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { useCheckoutFacade } from "../composables/useCheckoutFacade";
import { getGstBreakdown } from "../utils/gst-breakdown";

interface IProps {
  showTaxAndShipping?: boolean;
  includeVatInSubtotal?: boolean;
  footnote?: boolean;
  externalLoadingEvent?: boolean;
  showEstimatedWeight?: boolean;
}
const props = defineProps<IProps>();
const { t } = useI18n();
const { currentCurrency } = useCurrency();
const hasEnteredShippingAccountNumber = ref(false);
const hasEnteredTaxExemptNo = ref(false);

const {
  totalItems,
  subTotal,
  cartSubtotalWithTax,
  checkoutShipment,
  taxTotal,
  handlingTotal,
  discountTotal,
  discountTotalWithTax,
  hasDiscounts,
  hasOnlyNewLabDiscount,
  total,
  hasTaxExemptNo,
  changing,
  formattedCartWeight,
  cart,
  quoteOrder,
  isCustomerOrder,
} = useCheckoutFacade();
const { themeContext } = useThemeContext();

const useCnyExtraTaxPrice = computed(
  () => currentCurrency.value.code === "CNY" && props.includeVatInSubtotal && !!cartSubtotalWithTax.value,
);

const shippingPrice = computed(() => {
  return checkoutShipment.value.shipmentPriceAmount ?? 0;
});

const shouldDisplayShippingAccount = computed(() => {
  return hasEnteredShippingAccountNumber.value || (checkoutShipment.value?.shippingAccountNumberTL ?? false);
});

const hasAssessedTax = computed(() => {
  return (taxTotal.value?.amount ?? 0) > 0;
});

const shouldDisplayTaxExempt = computed(() => {
  return (hasEnteredTaxExemptNo.value || hasTaxExemptNo.value) && !hasAssessedTax.value;
});

// Carts tax the INR handling fee at the live INRTax store setting rate; quote orders use their snapshotted rate.
const cartHandlingFeeGstRate = computed<number>(() => {
  const rawJson = new FeatureFlags(themeContext.value?.storeSettings?.modules ?? []).getValue(
    "Thorlabs.Commerce",
    "INRTax",
  ) as string | null;
  if (!rawJson) {
    return 0;
  }
  try {
    return (JSON.parse(rawJson) as { HandlingFeeTaxRate?: number }).HandlingFeeTaxRate ?? 0;
  } catch {
    return 0;
  }
});

const gstBreakdown = computed(() => {
  if (currentCurrency.value.code !== "INR" || !hasAssessedTax.value || !checkoutShipment.value?.hasDeliveryAddress) {
    return [];
  }

  if (isCustomerOrder.value) {
    const order = quoteOrder.value;
    return getGstBreakdown(
      order?.items ?? [],
      sumBy(order?.shipments ?? [], (shipment) => shipment?.taxTotal?.amount ?? 0),
      order?.handlingTotal?.amount ?? 0,
      order?.handlingFeeTaxPercentRateTL ?? 0,
    );
  }

  return getGstBreakdown(
    cart.value?.items?.filter((item) => item.selectedForCheckout) ?? [],
    sumBy(cart.value?.shipments ?? [], (shipment) => shipment?.taxTotal?.amount ?? 0),
    cart.value?.handlingTotal?.amount ?? 0,
    cartHandlingFeeGstRate.value,
  );
});

const cartTotalForCartPage = computed(() => {
  // For cart page, calculate subtotal - discount

  const subtotal = useCnyExtraTaxPrice.value ? cartSubtotalWithTax.value : subTotal.value;

  // If there are no discounts, return subtotals
  if (!hasDiscounts.value) {
    return subtotal;
  }

  // If subtotal or discount total are missing, return subtotal
  if (!subtotal || !discountTotal) {
    return subtotal;
  }
  // Instead of creating a new object, let's use a deep copy of the subtotal
  // and just modify the amount
  const result = { ...subtotal };

  const discount = useCnyExtraTaxPrice.value ? discountTotalWithTax.value?.amount : discountTotal.value?.amount;

  result.amount = subtotal.amount - (discount ?? 0);

  return result;
});

const totalDisplay = computed(() => {
  return props.showTaxAndShipping ? total.value : cartTotalForCartPage.value;
});

const discountDisplay = computed(() => {
  return useCnyExtraTaxPrice.value ? discountTotalWithTax.value : discountTotal.value;
});

function getDiscountPolicyText() {
  const link = `<a href='/discount-policy' class='text-[--color-accent-500]'>Discount Policy</a>`;
  return t("pages.cart.text_tax1", [link]);
}

function orderSummaryMessage() {
  switch (currentCurrency.value.code) {
    case "CNY":
      return t("pages.cart.order_summary_message_cny");
    case "INR":
      return t("pages.cart.order_summary_message_inr");
    case "USD":
    case "EUR":
    case "SEK":
    case "GBP":
      return t("pages.cart.text_tax");
    case "JPY":
      return t("pages.cart.order_summary_message_jpy");
    case "BRL":
      return "";
  }
}

const taxLineText = computed(() => {
  switch (currentCurrency.value.code) {
    case "BRL":
      return t("common.labels.tax");
    case "INR":
      return t("common.labels.gst");
    case "USD":
    case "JPY":
      return t("common.labels.sales_tax");
    case "EUR":
    case "GBP":
    case "CNY":
    case "SEK":
      return t("common.labels.vat");
    default:
      return t("common.labels.tax");
  }
});

const checkoutOrderSummaryMessage = computed(() => {
  const messages = [];
  if (shouldDisplayShippingAccount.value && shouldDisplayTaxExempt.value) {
    messages.push(`<div class="mb-1.5">a.${t("shared.checkout.shipping_account_number_eligibility")}</div>`);
    messages.push(`<div>b.${t("shared.checkout.vat_eligibility")}</div>`);
  } else if (shouldDisplayShippingAccount.value) {
    messages.push(`*${t("shared.checkout.shipping_account_number_eligibility")}`);
  } else if (shouldDisplayTaxExempt.value) {
    messages.push(`*${t("shared.checkout.vat_eligibility")}`);
  }
  return messages.join("");
});

const shippingAccountNumberSymbol = computed(() => {
  return shouldDisplayShippingAccount.value && !shouldDisplayTaxExempt.value ? "*" : "<sup>a</sup>";
});

const taxExemptSymbol = computed(() => {
  return !shouldDisplayShippingAccount.value && shouldDisplayTaxExempt.value ? "*" : "<sup>b</sup>";
});

const shouldShowFootnote = computed(() => {
  if (!props.footnote) {
    return false;
  }

  return props.showTaxAndShipping ? !!checkoutOrderSummaryMessage.value : !!orderSummaryMessage();
});

onMounted(() => {
  eventBus.on("hasEnteredShippingAccountNumber", (payload) => {
    hasEnteredShippingAccountNumber.value = payload.hasEnteredShippingAccountNumber;
  });

  eventBus.on("hasTaxExemptNumber", (payload) => {
    hasEnteredTaxExemptNo.value = payload.hasTaxExemptNumber;
  });
});
</script>

<style lang="scss">
.cart {
  #order-summary {
    .total,
    .tax {
      @apply hidden;
    }
  }
}

.checkout-page {
  #order-summary {
    .after-discount {
      @apply hidden;
    }

    .policy-link {
      @apply text-sm leading-[22px];
    }

    sup {
      @apply text-xxs;
    }
  }
}

@media print {
  #order-summary {
    :deep(.vc-widget__header-container) {
      @apply hidden;
    }
  }
}

.policy-link {
  a {
    @apply font-bold;
  }
}
</style>
